import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const NAV = [
  { label: "Services", target: "services" },
  { label: "Work", target: "manifesto" },
  { label: "Reviews", target: "reviews" },
  { label: "Contact", target: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
    } else {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-[90rem] px-6 md:px-12">
        <div
          className={`flex items-center justify-between rounded-full px-5 md:px-7 py-3 transition-colors duration-500 ${
            scrolled ? "glass" : "bg-transparent border border-transparent"
          }`}
        >
          <Logo className="h-7" />

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((item) => (
              <button
                key={item.target}
                data-testid={`nav-${item.target}`}
                onClick={() => go(item.target)}
                className="font-mono text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/booking"
              data-testid="nav-book-btn"
              className="hidden sm:inline-flex items-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black hover:bg-white/90 transition-colors"
            >
              Book a Call
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-6 mt-2 rounded-3xl glass p-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-5">
              {NAV.map((item) => (
                <button
                  key={item.target}
                  onClick={() => go(item.target)}
                  className="text-left font-display text-2xl font-bold text-white"
                >
                  {item.label}
                </button>
              ))}
              <Link
                to="/booking"
                onClick={() => setOpen(false)}
                className="mt-2 rounded-full bg-white px-5 py-3 text-center font-semibold text-black"
              >
                Book a Call
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
